/**
 * Profile Social Route Definition
 */
import { createFileRoute } from '@tanstack/react-router';
import { v2 } from '@/lib/i18n/v2';
import { ErrorState } from '@/components/v2/error-state';
import { SocialFeedTemplatePending } from '@/components/v2/page-template-pending';
import { profileSocialQuery } from '@/lib/queries/detail';
import { cacheFirst } from '@/lib/queries/cache-first';
import { createProfileRetry } from './-profile-retry';

export function createSocialErrorComponent(invalidate: () => unknown) {
  return ({ reset }: { reset: () => void }) => (
    <div role="status">
      <ErrorState
        title={v2.error.loadFailed('your posts')}
        onRetry={createProfileRetry(reset, invalidate)}
      />
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/profile/social')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The destination is a feed (tabs + tall social cards), not a list of rows.
  pendingComponent: SocialFeedTemplatePending,
  loader: async ({ context }) => {
    // On client-side navigations this goes browser → Go API directly; only
    // SSR pays the cookie-forwarding proxy hop (see authenticated-shared).
    const refresh = () => context.queryClient.ensureInfiniteQueryData(profileSocialQuery());
    // Cache-first gate on the infinite feed: a cached feed resolves the loader
    // instantly and ensureInfiniteQueryData refreshes stale entries in the
    // background (single-flight-deduped with the mounted hook); a cold cache
    // keeps the streamed fetch the Await boundary renders against.
    const { cached, stream } = cacheFirst(context.queryClient, ['profile-posts', 'mine'], refresh);
    return { postsPromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
});
